import { Menu, Send, LogOut, Monitor, BellRing, Video, ChevronDown } from "lucide-react";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import logo from "../../assets/images/logo.png";
import { disconnect, getCookie } from "src/helpers/localStorage";
import { useTranslation } from "../../pages";
import { cn } from "src/lib/utils";

export const SidebarV2 = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [monitorOpen, setMonitorOpen] = useState(true);
  const location = useLocation();
  const { t } = useTranslation();

  const onLogOut = () => {
    disconnect();
    window.location.reload();
  };

  const searchParams = new URLSearchParams(location.search || "");
  const tokenParam = searchParams.get("token");
  const fallbackToken = getCookie("token");
  const tokenForV2Links = tokenParam || fallbackToken || "token";

  const withToken = (path) =>
    `${path}${path.includes("?") ? "&" : "?"}token=${encodeURIComponent(tokenForV2Links)}`;

  const monitorItemsV2 = [
    { icon: Send, label: "Suivi des vehicules", path: "/my-data/tracking-v2" },
    { icon: BellRing, label: "Event Center", path: "/my-data/event-center-v2" },
    { icon: Video, label: "Video", path: "/my-data/dashcam-v2" },
  ];
  const isMonitorActive = monitorItemsV2.some((item) => location.pathname === item.path);

  return (
    <aside
      className={cn(
        "relative z-40 flex h-screen shrink-0 flex-col overflow-visible bg-[#313131] text-[#cbd5e1] transition-all duration-200",
        collapsed ? "w-20" : "w-[80px]"
      )}
    >
      <div className="flex h-[50px] items-center justify-center bg-[#10b981] px-2">
        <img
          src={logo}
          alt="Logo"
          className={cn("object-contain", collapsed ? "h-full w-full" : "h-full w-full")}
        />
      </div>

      <nav className="relative z-40 min-h-0 flex-1 overflow-y-auto py-2">
        <button
          type="button"
          onClick={() => setMonitorOpen((prev) => !prev)}
          className={cn(
            "relative flex w-full flex-col items-center gap-2 px-2 py-4 text-center transition-colors duration-200",
            isMonitorActive
              ? "bg-[#191c1f] text-white"
              : "text-[#aeb9c5] hover:bg-[#383838] hover:text-white"
          )}
        >
          {isMonitorActive ? <span className="absolute left-0 top-0 h-full w-[3px] bg-[#10b981]" /> : null}
          <span className="grid h-7 w-7 place-items-center text-[#e5e7eb]">
            <Monitor className="h-5 w-5 shrink-0" />
          </span>
          {!collapsed ? (
            <span className={cn("flex items-center gap-1 text-[11px] leading-tight", isMonitorActive ? "font-semibold text-white" : "font-normal")}>
              Monitor
              <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", monitorOpen ? "rotate-180" : "")} />
            </span>
          ) : null}
        </button>

        {monitorOpen ? (
          <div className="mt-1 space-y-1">
            {monitorItemsV2.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={withToken(item.path)}
                  className={cn(
                    "relative flex w-full flex-col items-center gap-2 px-2 py-3 text-center transition-colors duration-200",
                    isActive
                      ? "bg-[#191c1f] text-white"
                      : "text-[#aeb9c5] hover:bg-[#383838] hover:text-white"
                  )}
                >
                  {isActive ? <span className="absolute left-0 top-0 h-full w-[3px] bg-[#10b981]" /> : null}
                  <span className="grid h-6 w-6 place-items-center text-[#e5e7eb]">
                    <item.icon className="h-4 w-4 shrink-0" />
                  </span>
                  {!collapsed ? (
                    <span className={cn("text-[10px] leading-tight", isActive ? "font-semibold text-white" : "font-normal")}>
                      {item.label}
                    </span>
                  ) : null}
                </Link>
              );
            })}
          </div>
        ) : null}
      </nav>

      <div className="border-t border-[#8a8a8a] p-2">
        <button
          type="button"
          onClick={onLogOut}
          className="mb-1 flex w-full flex-col items-center justify-center gap-2 py-3 text-[#e5e7eb] hover:bg-[#383838]"
          aria-label={t("sidebar.logout")}
        >
          <LogOut className="h-5 w-5 rotate-45" />
          {!collapsed ? <span className="text-[11px] leading-tight">{t("sidebar.logout")}</span> : null}
        </button>
      </div>

      <div className="border-t border-[#8a8a8a] p-2">
        <button
          type="button"
          className="flex w-full items-center justify-center py-3 text-[#e5e7eb] hover:bg-[#383838]"
          aria-label={collapsed ? t("sidebar.menu.open") : t("sidebar.menu.close")}
          onClick={() => setCollapsed((prev) => !prev)}
        >
          <Menu className="h-5 w-5" />
        </button>
      </div>
    </aside>
  );
};

export default SidebarV2;
